/* BrainGain Find Your Pair v4.1. Segmented selector; one purchase action. */
(function () {
  'use strict';
  const STEPS = [2, 1.5, 1];
  const byPrice = (a, b) => a.price - b.price || a.title.localeCompare(b.title);
  const band = weight => Math.ceil(weight / 5) * 5;
  function matchProducts(products, state) {
    if (!products.length) return { products: [], relaxed: [], band: null };
    const bands = [...new Set(products.map(p => band(p.max)))].sort((a, b) => a - b);
    const chosen = bands.includes(state.weight) ? state.weight : (bands.filter(n => n < state.weight).pop() || bands[0]);
    const pool = products.filter(p => band(p.max) === chosen), relaxed = [];
    const matches = () => pool.filter(p =>
      (relaxed.includes('material') || state.material === 'any' || p.material === state.material) &&
      (relaxed.includes('finish') || state.finish === 'any' || p.finish === state.finish) &&
      (relaxed.includes('increment') || p.step === state.increment));
    let result = matches();
    for (const field of ['material', 'finish', 'increment']) {
      if (result.length) break;
      if (state[field] === 'any') continue;
      relaxed.push(field); result = matches();
    }
    return { products: result.sort(byPrice), relaxed, band: chosen };
  }
  function purchasePlan(pair, accessories, state) {
    if (!pair) return null;
    const stand = state.stand && accessories.find(p => p.kind === 'stand' && p.range === pair.range);
    const bench = state.bench && accessories.find(p => p.kind === 'bench');
    const candidate = state.bench && pair.key === '415_chrome' && accessories.find(p => p.kind === 'bundle');
    // A bundle is one sellable Shopify product: never add its constituent pair or bench again.
    const bundle = candidate?.available && bench && candidate.price <= pair.price + bench.price ? candidate : null;
    const items = bundle ? [bundle] : [pair, ...(bench ? [bench] : [])];
    if (stand) items.push(stand);
    return {
      pair, stand: stand || null, bench: bench || null, bundle,
      items, price: items.reduce((n, p) => n + p.price, 0),
      compare: items.reduce((n, p) => n + Math.max(p.price, p.compare || 0), 0),
      saving: bundle ? Math.max(0, pair.price + bench.price - bundle.price) : 0,
      available: items.every(p => p.available)
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { matchProducts, purchasePlan, band };
  if (typeof window === 'undefined' || customElements.get('smartbells-finder-v4')) return;
  const requests = new Map();
  async function getJSON(url, options = {}) {
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
      if (response.status === 404 || response.status === 410) return null;
      if (!response.ok) throw new Error('HTTP ' + response.status);
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function imageURL(source, width) {
    try {
      const u = new URL(source.startsWith('//') ? 'https:' + source : source, location.origin);
      if (!['http:', 'https:'].includes(u.protocol)) return '';
      u.searchParams.set('width', width); return u.href;
    } catch (_) { return ''; }
  }
  class Finder extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      if (!this.querySelector('[data-config]')) {
        if (!this.parseObserver) {
          this.parseObserver = new MutationObserver(() => this.connectedCallback());
          this.parseObserver.observe(this, { childList: true, subtree: true });
        }
        return;
      }
      this.parseObserver?.disconnect();
      try { this.config = JSON.parse(this.querySelector('[data-config]').textContent); }
      catch (error) { console.warn('BrainGain finder configuration', error); return; }
      this.initialised = true;
      this.root = (this.config.root || '/').replace(/\/?$/, '/');
      this.state = { weight: 45, increment: 1.5, finish: 'any', material: 'any', stand: false, bench: false };
      this.products = []; this.accessories = []; this.selectedKey = null;
      this.dialog = this.querySelector('dialog');
      this.error = this.querySelector('[data-error]');
      try {
        this.currency = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
        this.number = new Intl.NumberFormat(this.config.locale, { maximumFractionDigits: 1 });
      } catch (_) {
        this.currency = new Intl.NumberFormat('en', { style: 'currency', currency: 'EUR' });
        this.number = new Intl.NumberFormat('en');
      }
      this.querySelectorAll('[data-text]').forEach(n => { n.textContent = this.t(n.dataset.text); });
      this.querySelector('[data-close]').setAttribute('aria-label', this.t('close'));
      this.addEventListener('input', event => {
        const input = event.target.closest('[data-slider]');
        if (!input || this.busy || this.loading) return;
        this.state[input.dataset.slider] = input.dataset.slider === 'weight' ? Number(input.value) : STEPS[Number(input.value)];
        this.render();
      });
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-addon]');
        if (!input || this.busy || this.loading) return;
        this.state[input.dataset.addon] = input.checked; this.render();
      });
      this.addEventListener('click', event => {
        if (event.target.closest('[data-open]')) this.open();
        if (event.target.closest('[data-close]')) this.close();
        const pill = event.target.closest('[data-field]');
        if (pill && !this.busy && !this.loading) {
          this.state[pill.dataset.field] = pill.dataset.value; this.render();
        }
        const select = event.target.closest('[data-select]');
        if (select && !this.busy && !this.loading) {
          this.selectedKey = select.dataset.select; this.render();
          this.querySelector('[data-select="' + this.selectedKey + '"]')?.focus({ preventScroll: true });
        }
        const preset = event.target.closest('[data-preset]');
        if (preset && !preset.disabled && !this.busy && !this.loading) {
          const mode = preset.dataset.preset;
          this.state.stand = mode === 'stand' || mode === 'full';
          this.state.bench = mode === 'bench' || mode === 'full';
          this.render();
          this.querySelector('[data-preset="' + mode + '"]')?.focus({ preventScroll: true });
        }
        const buy = event.target.closest('[data-buy]');
        if (buy && !buy.disabled) this.add(buy.dataset.buy, buy);
        if (event.target.closest('[data-retry]') && !this.busy) this.load(true);
      });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const focusable = [...this.dialog.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),[tabindex="0"]')].filter(n => n.getClientRects().length && !n.closest('[hidden]'));
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      });
      this.dialog.addEventListener('close', () => this.unlock());
      this.load();
    }
    disconnectedCallback() { this.parseObserver?.disconnect(); this.close(false); }
    open() {
      if (this.dialog.open) return;
      this.returnFocus = document.activeElement;
      this.dialog.showModal();
      this.savedOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.locked = true;
    }
    unlock() {
      if (!this.locked) return;
      document.documentElement.style.overflow = this.savedOverflow;
      this.locked = false;
    }
    close(restore = true) {
      if (this.dialog?.open) this.dialog.close();
      this.unlock();
      if (restore && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }
    t(key, values = {}) {
      let text = this.config.strings[key] || key;
      for (const [name, value] of Object.entries(values)) text = text.split('{' + name + '}').join(String(value));
      return text;
    }
    money(value) { return this.currency.format(value / 100); }
    n(value) { return this.number.format(value); }
    short(pair) { return ({ smartbells: 'Evo', pro: 'Pro', ninja: 'Ninja' })[pair.range] + ' ' + this.n(pair.max) + ' kg' + (pair.range === 'pro' ? ' · ' + this.t(pair.finish) : ''); }
    title(pair) { return 'SmartBells™ ' + ({ smartbells: 'Evo', pro: 'Pro', ninja: 'Ninja' })[pair.range] + ' ' + this.n(pair.max) + ' kg ' + this.t('adjustable_dumbbells') + (pair.range === 'pro' ? ' – ' + this.t(pair.finish) : ''); }
    spec(pair) { return this.t('spec', { min: this.n(pair.min), max: this.n(pair.max), step: this.n(pair.step), count: pair.weights, material: this.t(pair.material) }); }
    async resolve(definition, fresh = false) {
      if (!definition?.handle) return null;
      const url = this.root + 'products/' + encodeURIComponent(definition.handle) + '.js';
      const key = this.config.country + '|' + url;
      let entry = requests.get(key);
      if (fresh || !entry || Date.now() - entry.time > 60000) {
        entry = { time: Date.now(), promise: getJSON(url, { cache: 'no-store' }) };
        requests.set(key, entry);
        entry.promise.catch(() => { if (requests.get(key) === entry) requests.delete(key); });
      }
      const product = await entry.promise;
      if (!product) return null;
      if (!product.id || !Array.isArray(product.variants) || (definition.id && String(definition.id) !== String(product.id))) throw new Error('Unexpected product');
      const variant = product.variants.find(v => v.available) || product.variants[0];
      if (!variant || !Number.isFinite(variant.price)) throw new Error('Missing variant');
      let rating = Number(definition.rating), reviews = Number(definition.reviews);
      if ((!rating || !reviews) && definition.reviewBadge) {
        const template = document.createElement('template'); template.innerHTML = definition.reviewBadge;
        const badge = template.content.querySelector('[data-average-rating]');
        if (badge) { rating = Number(badge.dataset.averageRating); reviews = Number(badge.dataset.numberOfReviews); }
      }
      return {
        ...definition, id: product.id, title: product.title, variant: variant.id,
        price: variant.price, compare: variant.compare_at_price || 0,
        available: !!variant.available && !product.requires_selling_plan,
        image: definition.image || product.featured_image || '',
        url: this.root + 'products/' + encodeURIComponent(product.handle) + '?variant=' + variant.id,
        rating, reviews, definition
      };
    }
    async load(fresh = false) {
      this.loading = true; this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('fieldset,[data-buy],[data-select],[data-preset]').forEach(n => { n.disabled = true; });
      this.status('loading');
      const results = await Promise.allSettled(this.config.items.map(d => this.resolve(d, fresh)));
      this.failed = results.some(r => r.status === 'rejected');
      this.failedNinja = results.some((r, i) => r.status === 'rejected' && this.config.items[i].range === 'ninja' && this.config.items[i].kind === 'pair');
      const all = results.filter(r => r.status === 'fulfilled' && r.value).map(r => r.value);
      this.products = all.filter(p => p.kind === 'pair');
      this.accessories = all.filter(p => p.kind !== 'pair');
      this.loading = false; this.removeAttribute('aria-busy');
      this.status(this.failed ? 'load_error' : null);
      this.render();
    }
    status(key) {
      this.querySelectorAll('[data-status],[data-modal-status]').forEach(node => {
        node.replaceChildren();
        if (!key) return;
        node.append(el('span', '', this.t(key) + ' '));
        if (key === 'load_error') {
          const b = el('button', 'bgv4-retry', this.t('retry')); b.type = 'button'; b.dataset.retry = ''; node.append(b);
        }
      });
    }
    controls(pair) {
      this.querySelectorAll('fieldset').forEach(n => { n.disabled = !!(this.busy || this.loading); });
      this.querySelectorAll('[data-slider]').forEach(input => {
        const field = input.dataset.slider, value = this.state[field];
        input.value = field === 'weight' ? value : STEPS.indexOf(value);
        input.closest('.bgv4-control').querySelector('[data-value-for]').textContent = this.n(value) + ' kg';
        input.style.setProperty('--progress', (field === 'weight' ? (value - 25) * 5 : STEPS.indexOf(value) * 50) + '%');
        input.setAttribute('aria-valuetext', this.n(value) + ' kg' + (field === 'increment' ? ' · ' + ({ 2: 'Evo', 1.5: 'Pro', 1: 'Ninja' })[value] : ''));
        input.closest('.bgv4-control').querySelectorAll('[data-stop]').forEach(stop => {
          const n = Number(stop.dataset.stop);
          stop.classList.toggle('is-active', n === value);
          stop.textContent = this.n(n) + ' kg' + (field === 'increment' ? ' · ' + ({ 2: 'Evo', 1.5: 'Pro', 1: 'Ninja' })[n] : '');
        });
      });
      this.querySelectorAll('[data-field]').forEach(button => button.setAttribute('aria-pressed', String(this.state[button.dataset.field] === button.dataset.value)));
      for (const kind of ['stand', 'bench']) {
        const input = this.querySelector('[data-addon="' + kind + '"]');
        const pool = this.accessories.filter(p => p.kind === kind && (kind === 'bench' || this.products.some(pair => pair.range === p.range)));
        const matching = kind === 'stand' && pair ? pool.find(p => p.range === pair.range) : pool[0];
        input.closest('label').hidden = !pool.length;
        input.checked = this.state[kind] && !!matching;
        const bundleAvailable = kind === 'bench' && pair?.key === '415_chrome' && this.accessories.some(p => p.kind === 'bundle' && p.available && p.price <= pair.price + (matching?.price || 0));
        // An add-on that sells out while selected must still be removable.
        input.disabled = (!matching || (!matching.available && !bundleAvailable)) && !this.state[kind];
        const price = matching ? matching.price : Math.min(...pool.map(p => p.price));
        this.querySelector('[data-addon-price="' + kind + '"]').textContent = pool.length ? '+' + this.money(price) : '';
      }
      const plan = purchasePlan(pair, this.accessories, this.state), notes = [];
      if (plan && !plan.stand && !this.accessories.some(p => p.kind === 'stand' && p.range === pair?.range)) notes.push(this.t('no_stand'));
      if (plan?.stand && !plan.stand.available) notes.push(this.t('stand_sold_out'));
      if (this.state.bench && plan && !plan.bench) notes.push(this.t('no_bench'));
      if (plan?.bench && !plan.bench.available && !plan.bundle) notes.push(this.t('bench_sold_out'));
      this.querySelector('[data-addon-note]').textContent = notes.join(' ');
    }
    render() {
      const match = matchProducts(this.products, this.state);
      if (!match.products.some(p => p.key === this.selectedKey)) this.selectedKey = match.products[0]?.key || null;
      const pair = match.products.find(p => p.key === this.selectedKey);
      // Accessories follow the chosen pair; never retain an invisible selected add-on.
      if (pair && !this.accessories.some(p => p.kind === 'stand' && p.range === pair.range)) this.state.stand = false;
      if (!this.accessories.some(p => p.kind === 'bench')) this.state.bench = false;
      this.controls(pair);
      const count = match.products.length;
      const fallback = match.relaxed.length > 0 || (match.band && match.band !== this.state.weight);
      this.querySelector('[data-count]').textContent = this.t(fallback ? (count === 1 ? 'closest_one' : 'closest_many') : (count === 1 ? 'count_one' : 'count_many'), { count });
      this.querySelector('[data-compact-count]').textContent = this.t(fallback ? (count === 1 ? 'closest_one' : 'closest_many') : (count === 1 ? 'compact_one' : 'compact_many'), { count });
      const notes = [];
      const changes = [];
      if (match.band && match.band !== this.state.weight) changes.push(this.t('other_band', { weight: this.n(Math.max(...match.products.map(p => p.max))) }));
      const noNinja = this.state.increment === 1 && !this.failedNinja && !this.products.some(p => p.range === 'ninja');
      if (noNinja) notes.push(this.t('no_ninja'));
      if (match.relaxed.includes('material')) changes.push(this.t('other_materials'));
      if (match.relaxed.includes('finish')) changes.push(this.t('other_finishes'));
      if (match.relaxed.includes('increment')) changes.push(this.t('other_steps', { steps: [...new Set(match.products.map(p => p.step))].map(n => this.n(n)).join(' / ') }));
      if (changes.length) notes.push(this.t('relaxed_summary', { changes: changes.join(', ') }));
      this.querySelectorAll('[data-note],[data-compact-note]').forEach(n => { n.textContent = notes.join(' '); });
      const best = this.querySelector('[data-best]'); best.replaceChildren();
      // The compact card always reports the cheapest match; the modal remembers the chosen pair.
      if (count) this.bestCard(best, match.products[0], count);
      else best.append(el('p', 'bgv4-note', this.t(this.failed ? 'load_error' : 'no_pairs')));
      const grid = this.querySelector('[data-results]'); grid.replaceChildren();
      for (const p of match.products) grid.append(this.card(p));
      if (!count) grid.append(el('p', 'bgv4-note', this.t(this.failed ? 'load_error' : 'no_pairs')));
      const setups = this.querySelector('[data-setups]'); setups.replaceChildren();
      const setupFor = this.querySelector('[data-setup-for]'); setupFor.replaceChildren();
      if (pair) {
        setupFor.textContent = this.t('for_pair', { pair: this.short(pair) });
        const stand = this.accessories.find(p => p.kind === 'stand' && p.range === pair.range);
        const bench = this.accessories.find(p => p.kind === 'bench');
        if (stand) setups.append(this.setupCard(pair, 'stand'));
        if (bench) setups.append(this.setupCard(pair, 'bench'));
        if (stand && bench) setups.append(this.setupCard(pair, 'full'));
        if (!stand && !bench) setups.append(el('p', 'bgv4-note', this.t('no_setups')));
      }
      this.querySelector('.bgv4-setups').hidden = !pair;
      const plan = purchasePlan(pair, this.accessories, this.state);
      const summary = this.querySelector('[data-selection]'); summary.replaceChildren();
      const total = this.querySelector('[data-total]'); total.replaceChildren();
      const footer = this.querySelector('[data-buy="selected"]');
      footer.disabled = !plan?.available || !!this.busy || !!this.loading || !!this.cartUncertain;
      footer.textContent = this.busy ? this.t('adding') : (plan && !plan.available ? this.t('sold_out') : this.t(plan?.stand || plan?.bench ? 'add_setup' : 'add_cart') + ' →');
      if (pair) {
        const labels = [plan.stand ? this.t('stand') : '', plan.bench ? this.t('bench') : ''].filter(Boolean);
        const caption = el('div');
        caption.append(el('strong', '', this.short(pair) + (labels.length ? ' + ' + labels.join(' + ') : '')));
        caption.append(el('span', plan.saving > 0 ? 'bgv4-saving' : 'bgv4-note', this.t(plan.saving > 0 ? 'bundle_applied' : labels.length ? 'complete_setups' : 'no_addons')));
        summary.append(this.picture(pair, 'bgv4-footer-image', 120), caption);
        total.append(el('span', 'bgv4-total-label', this.t('total')), this.prices(plan));
      }
    }
    picture(product, className, width = 600) {
      const wrap = el('div', className), source = imageURL(product.image, width);
      if (source) {
        const image = el('img'); image.src = source; image.alt = product.title;
        image.loading = 'lazy'; image.decoding = 'async'; image.width = width; image.height = width;
        wrap.append(image);
      }
      return wrap;
    }
    prices(plan) {
      const row = el('p', 'bgv4-price');
      const current = el('span', '', this.money(plan.price)); current.setAttribute('aria-label', this.t('current_price', { price: this.money(plan.price) })); row.append(current);
      if (plan.compare > plan.price) {
        const old = el('s', '', this.money(plan.compare)); old.setAttribute('aria-label', this.t('regular_price', { price: this.money(plan.compare) })); row.append(old);
      }
      return row;
    }
    bestCard(container, pair) {
      container.append(this.picture(pair, 'bgv4-best-image', 300));
      const content = el('div', 'bgv4-best-content');
      content.append(el('p', 'bgv4-range', this.t('best_match')), el('strong', 'bgv4-best-name', this.short(pair)), el('p', 'bgv4-note', this.t('steps_short', { step: this.n(pair.step) })), this.prices(pair));
      const link = el('a', 'bgv4-text-link', this.t('view_pair') + ' →'); link.href = pair.url;
      content.append(link); container.append(content);
    }
    card(pair) {
      const selected = pair.key === this.selectedKey, card = el('article', 'bgv4-pair' + (selected ? ' is-selected' : ''));
      const choose = el('button', 'bgv4-select'); choose.type = 'button'; choose.dataset.select = pair.key;
      choose.disabled = !!this.busy || !!this.loading;
      choose.setAttribute('aria-pressed', String(selected)); choose.setAttribute('aria-label', this.t('select_pair', { pair: this.title(pair) }));
      card.append(choose);
      const body = el('div', 'bgv4-pair-body');
      const media = this.picture(pair, 'bgv4-pair-image');
      if (pair.range === 'ninja') media.append(el('span', 'bgv4-ninja-badge', this.t('ninja_badge')));
      const content = el('div', 'bgv4-pair-content');
      content.append(el('p', 'bgv4-range', this.t('range_' + pair.range)), el('h4', 'bgv4-pair-title', this.short(pair)));
      content.append(el('p', 'bgv4-spec', this.n(pair.min) + '–' + this.n(pair.max) + ' kg · ' + this.t('steps_short', { step: this.n(pair.step) })));
      content.append(el('p', 'bgv4-spec', this.t('weights_short', { count: pair.weights }) + ' · ' + this.t(pair.material)));
      if (pair.rating > 0 && pair.reviews > 0) {
        const rating = el('p', 'bgv4-rating'), stars = el('span', 'bgv4-stars', '★★★★★');
        stars.style.setProperty('--rating', Math.min(100, pair.rating / 5 * 100) + '%');
        stars.setAttribute('aria-label', this.t('rating', { rating: this.n(pair.rating) }));
        const count = el('span', '', this.n(pair.rating) + ' (' + this.n(pair.reviews) + ')');
        count.setAttribute('aria-label', this.t('reviews', { count: this.n(pair.reviews) }));
        rating.append(stars, count); content.append(rating);
      }
      content.append(this.prices(pair));
      if (!pair.available) content.append(el('p', 'bgv4-note', this.t('sold_out')));
      const link = el('a', 'bgv4-text-link', this.t('details') + ' →'); link.href = pair.url;
      content.append(link); body.append(media, content); card.append(body);
      return card;
    }
    addonName(item) { return this.t(item.kind === 'stand' ? 'stand_' + item.range + '_name' : 'bench'); }
    planFor(pair, mode) { return purchasePlan(pair, this.accessories, mode === 'selected' ? this.state : { stand: mode === 'stand' || mode === 'full', bench: mode === 'bench' || mode === 'full' }); }
    setupCard(pair, mode) {
      const plan = this.planFor(pair, mode);
      const selected = this.state.stand === (mode === 'stand' || mode === 'full') && this.state.bench === (mode === 'bench' || mode === 'full');
      const card = el('article', 'bgv4-setup' + (selected ? ' is-selected' : '') + (!plan.available ? ' is-unavailable' : ''));
      card.dataset.setup = mode;
      const button = el('button', 'bgv4-preset'); button.type = 'button'; button.dataset.preset = mode;
      button.disabled = !plan.available || !!this.busy || !!this.loading;
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', this.t('select_setup') + ': ' + this.t(mode === 'full' ? 'full_setup' : 'pair_' + mode) + ', ' + this.money(plan.price));
      card.append(button, el('span', 'bgv4-card-status', this.t(selected ? 'selected' : 'select_setup')));
      const images = el('div', 'bgv4-setup-images');
      [pair, plan.stand, plan.bench].filter(Boolean).forEach((p, i) => {
        if (i) images.append(el('span', 'bgv4-plus', '+'));
        images.append(this.picture(p, 'bgv4-setup-image', 220));
      });
      const content = el('div', 'bgv4-setup-content');
      content.append(el('h4', 'bgv4-setup-title', this.t(mode === 'full' ? 'full_setup' : 'pair_' + mode)), this.prices(plan));
      if (plan.saving > 0) content.append(el('p', 'bgv4-saving', this.t('bundle_short', { saving: this.money(plan.saving) })));
      const breakdown = plan.items.map(item => this.t(item.kind === 'pair' ? 'pair_label' : item.kind === 'bundle' ? 'bundle_label' : item.kind) + ' ' + this.money(item.price)).join(' + ');
      content.append(el('p', 'bgv4-breakdown', breakdown));
      if (!plan.available) content.append(el('p', 'bgv4-note', this.t('sold_out')));
      card.append(images, content);
      return card;
    }

    showError(message, cartLink = false) {
      this.error.replaceChildren(el('span', '', message + ' '));
      if (cartLink) { const a = el('a', '', this.t('view_cart')); a.href = this.root + 'cart'; this.error.append(a); }
      if (!this.dialog.open) this.open();
      this.error.hidden = false; this.error.focus({ preventScroll: true });
    }
    async add(key, button) {
      if (this.busy || this.loading || this.cartUncertain) return;
      const [pairKey, mode] = key === 'selected' ? [this.selectedKey, 'selected'] : key.split(':');
      const pair = this.products.find(p => p.key === pairKey), plan = this.planFor(pair, mode);
      if (!plan?.available) return;
      this.busy = true; this.error.hidden = true;
      this.querySelectorAll('fieldset,[data-buy],[data-select],[data-preset]').forEach(n => { n.disabled = true; });
      button.textContent = this.t('adding');
      let postStarted = false, added = false;
      try {
        const fresh = await Promise.all(plan.items.map(p => this.resolve(p.definition, true)));
        if (fresh.some((p, i) => !p?.available || p.variant !== plan.items[i].variant || p.price !== plan.items[i].price)) {
          await this.load(true); this.showError(this.t('changed')); return;
        }
        const oldCart = await getJSON(this.root + 'cart.js', { cache: 'no-store' });
        if (!oldCart?.items) throw new Error('Cart unavailable');
        const drawer = document.querySelector('cart-drawer');
        const sections = typeof drawer?.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map(s => s.section) : ['cart-icon-bubble'];
        const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 20000);
        let response, data;
        try {
          postStarted = true;
          // No automatic POST retry. A lost response may still mean the cart changed.
          response = await fetch(this.root + 'cart/add.js', {
            method: 'POST', credentials: 'same-origin', signal: controller.signal,
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: plan.items.map(p => ({ id: p.variant, quantity: 1 })), sections, sections_url: location.pathname })
          });
          data = await response.json();
        } finally { clearTimeout(timeout); }
        if (!response.ok || data.status) {
          this.cartUncertain = true;
          this.showError((typeof data.description === 'string' ? data.description + ' ' : '') + this.t('cart_error'), true);
          this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: this.t('cart_error') } }));
          return;
        }
        added = true;
        // Leave the top layer before opening the theme's drawer, so the cart stays interactive.
        this.close();
        try {
          if (!drawer || typeof drawer.renderContents !== 'function' || !sections.every(s => data.sections?.[s])) throw new Error('Drawer unavailable');
          await drawer.renderContents(data, true);
          const cart = await getJSON(this.root + 'cart.js', { cache: 'no-store' });
          if (cart) for (const item of plan.items) {
            const previous = oldCart.items.filter(i => i.variant_id === item.variant).reduce((n, i) => n + i.quantity, 0);
            const quantity = cart.items.filter(i => i.variant_id === item.variant).reduce((n, i) => n + i.quantity, 0);
            this.dispatchEvent(new CustomEvent(previous ? 'on:line-item:change' : 'on:cart:add', { bubbles: true, detail: { cart, variantId: item.variant, oldQuantity: previous, newQuantity: quantity } }));
          }
        } catch (_) { location.assign(this.root + 'cart'); }
      } catch (_) {
        if (added) location.assign(this.root + 'cart');
        else { this.cartUncertain = postStarted; this.showError(this.t(postStarted ? 'cart_error' : 'network_error'), postStarted); }
      } finally {
        this.busy = false; this.render();
        if (!this.error.hidden && this.dialog.open) this.error.focus({ preventScroll: true });
      }
    }
  }
  customElements.define('smartbells-finder-v4', Finder);
})();
